import {DatePicker} from '@github-ui/date-picker'
import {AlertIcon, GlobeIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, useCallback, useId, useMemo, useState} from 'react'

import {formatDateString} from '../../utils/date-formatting'
import {getVisibilityIndicatorTooltip, LABELS} from './constants/labels'
import styles from './IssueFieldItem.module.css'
import {TooltipTriggerWrapper} from './utils/TooltipTriggerWrapper'

// Normalized hour for timezone-safe date storage (noon UTC)
const NORMALIZED_HOUR = 12

export type IssueFieldDateEditorProps = {
  fieldId: string
  fieldName: string
  value: Date | null
  onCommit: (fieldId: string, value: Date | null) => void
  shouldOpen?: boolean
  isDraft?: boolean
  readonly: boolean
  errorPresent?: boolean
  className?: string
  showVisibilityIndicator?: boolean
  isOwnerEnterpriseManaged?: boolean
}

type IssueFieldDateEditorButtonProps = {
  anchorProps: ComponentPropsWithoutRef<'button'>
  fieldName: string
  value: Date | null
  displayValue: string
  formattedDate: string
  isOpen: boolean
  errorPresent: boolean
  className?: string
  showVisibilityIndicator: boolean
  visibilityTooltipId?: string
}

const IssueFieldDateEditorButton = ({
  anchorProps,
  fieldName,
  value,
  displayValue,
  formattedDate,
  isOpen,
  errorPresent = false,
  className,
  showVisibilityIndicator,
  visibilityTooltipId,
}: IssueFieldDateEditorButtonProps) => {
  const content = (
    <button
      {...anchorProps}
      aria-describedby={visibilityTooltipId}
      className={clsx(
        styles.issueFieldFormWrapper,
        styles.issueFieldFormWrapperClickable,
        isOpen && styles.isEditing,
        className,
      )}
      aria-label={value ? `Edit ${fieldName}` : `Set ${fieldName}`}
    >
      <span className={styles.issueFieldLabel}>
        {fieldName}
        {showVisibilityIndicator && (
          <span className={styles.visibilityIconWrapper}>
            <GlobeIcon size={12} />
          </span>
        )}
      </span>
      <span className={clsx(styles.issueFieldValueText, formattedDate && styles.hasValue)}>{displayValue}</span>
      {errorPresent && (
        <span className={styles.alertIconWrapper}>
          <AlertIcon className={styles.alertIcon} />
        </span>
      )}
    </button>
  )

  return errorPresent ? (
    <Tooltip text={LABELS.errorUpdatingIssueField} type="description">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

export const IssueFieldDateEditor = ({
  fieldId,
  fieldName,
  value,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  readonly,
  errorPresent = false,
  className,
  showVisibilityIndicator = false,
  isOwnerEnterpriseManaged = false,
}: IssueFieldDateEditorProps) => {
  const visibilityTooltipId = useId()
  const [isOpen, setIsOpen] = useState<boolean>(shouldOpen)

  const formattedDate = useMemo(() => {
    if (!value) return ''
    return formatDateString(value)
  }, [value])

  // Convert UTC-normalized date to local date for DatePicker display
  const datePickerValue = useMemo(() => {
    if (!value) return null
    // Extract UTC date components and create local Date with same calendar date
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [value])

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
    setIsOpen(false)
  }, [isDraft, fieldId, onCommit])

  const handleDateChange = useCallback(
    (date: Date | null) => {
      // Normalize the date to avoid timezone issues
      let normalizedDate: Date | null = null
      if (date) {
        normalizedDate = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0),
        )
      }
      onCommit(fieldId, normalizedDate)
      setIsOpen(false)
    },
    [fieldId, onCommit],
  )

  const readonlyDisplayValue = formattedDate || LABELS.setValue
  const editableDisplayValue = formattedDate || LABELS.emptyValueLabels.date

  if (readonly) {
    return (
      <div className={clsx(styles.issueFieldFormWrapper, className)}>
        <p className={styles.issueFieldLabel}>{fieldName}</p>
        <div className={clsx(styles.issueFieldValueText, formattedDate && styles.hasValue)}>{readonlyDisplayValue}</div>
      </div>
    )
  }

  return (
    <DatePicker
      variant="single"
      showClearButton
      value={datePickerValue}
      onChange={handleDateChange}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={onClose}
      anchor={anchorProps => {
        const anchor = (
          <IssueFieldDateEditorButton
            anchorProps={anchorProps}
            fieldName={fieldName}
            value={value}
            displayValue={editableDisplayValue}
            formattedDate={formattedDate}
            isOpen={isOpen}
            errorPresent={errorPresent}
            className={className}
            showVisibilityIndicator={showVisibilityIndicator}
            visibilityTooltipId={showVisibilityIndicator ? visibilityTooltipId : undefined}
          />
        )
        return showVisibilityIndicator ? (
          <Tooltip
            id={visibilityTooltipId}
            text={getVisibilityIndicatorTooltip(isOwnerEnterpriseManaged)}
            type="description"
            direction="e"
            delay="medium"
          >
            <TooltipTriggerWrapper>{anchor}</TooltipTriggerWrapper>
          </Tooltip>
        ) : (
          anchor
        )
      }}
    />
  )
}
